import type { CSSProperties } from "react";

type SurfaceSkeletonProps = {
  /** Verb shown in the footer, e.g. "Reading", "Composing". Defaults to "Composing". */
  label?: string;
  /** The lens being loaded, e.g. "inbox", "calendar". Renders as "{label} your {lens}…". */
  lens?: string;
  /** Forwarded to the root as `data-testid` so existing loading tests keep working. */
  testId?: string;
};

/**
 * Full-bleed loading state for every surface: a brief-shaped shimmer, never a spinner.
 *
 * Renders the same `.deskbrief` / `.db-*` shell the real surfaces use, so it centers
 * itself and fills the panel. The shimmer leans into the product feel just a little,
 * and honors `prefers-reduced-motion` (the shimmer freezes, the layout stays).
 *
 * @example
 * <SurfaceSkeleton label="Reading" lens="inbox" testId="inbox-loading" />
 * // footer reads: "Reading your inbox…"
 */
export function SurfaceSkeleton({
  label = "Composing",
  lens,
  testId,
}: SurfaceSkeletonProps) {
  return (
    <main
      className="deskbrief sk-surface"
      data-testid={testId}
      style={{ "--dc": "var(--accent)" } as CSSProperties}
    >
      <div className="db-strip">
        <span className="db-strip-name sk-pill" style={{ width: 120 }} />
        <span className="db-strip-r">
          <span className="sk-pill" style={{ width: 70, height: 14 }} />
        </span>
      </div>
      <div className="db-scroll">
        <div className="db-col">
          <section className="db-brief">
            <div className="db-field">
              <div className="sk-line lab" />
              <div className="sk-line head" />
            </div>
            <div className="db-field">
              <div className="sk-line lab" />
              <div className="sk-line" />
              <div className="sk-line" style={{ width: "72%" }} />
            </div>
            <div className="db-field">
              <div className="sk-line lab" style={{ width: 60 }} />
              <div className="db-cards">
                {[0, 1].map((i) => (
                  <div key={i} className="sk-card">
                    <div className="sk-line" style={{ width: 130, height: 11 }} />
                    <div
                      className="sk-line head"
                      style={{ width: "80%", marginTop: 14 }}
                    />
                    <div
                      className="sk-line"
                      style={{ width: "45%", marginTop: 10 }}
                    />
                    <div className="sk-btn" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
      <div className="db-foot">
        <div className="db-foot-inner">
          <div className="sk-compose">
            <span className="sk-shimmer-dot" /> {label} your {lens || "brief"}…
          </div>
        </div>
      </div>
    </main>
  );
}
